import React, { useState, useEffect, useRef, ChangeEvent } from 'react';
import {
    Button,
    Card,
    FormControl,
    FormLabel,
    Grid,
    Input,
    Typography,
    Box,
    Stack,
} from '@mui/joy';
import axios from 'axios';
import 'mapbox-gl/dist/mapbox-gl.css';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {Map,Marker} from 'react-map-gl/mapbox';

import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';

import { useEventForm } from '../../context/EventFormContext';
import Logo from "../../assets/l-logo.svg";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

const EventLocationStep: React.FC = () => {
    const { secondFormData, updateSecondFormData, localisations, validateStep2, setStep } = useEventForm();
    const [query, setQuery] = useState<string>('');
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const cancelTokenRef = useRef<any>(null);
    const autocompleteRef = useRef<HTMLDivElement>(null);
    const [viewState, setViewState] = useState({
        latitude: 46.59822, // Coordonnée initiale (ex: Paris)
        longitude: 2.49561,
        zoom: 5,
    });

    // Pré-remplissage de l'input d'adresse à l'édition
    useEffect(() => {
        if (secondFormData.address || secondFormData.postal_code || secondFormData.city) {
            const formattedAddress = `${secondFormData.address || ''} ${secondFormData.postal_code || ''} ${secondFormData.city || ''}`.trim();
            setQuery(formattedAddress);
        }
    }, [secondFormData.address, secondFormData.postal_code, secondFormData.city]);

    // Requête de recherche d'adresses
    useEffect(() => {
        if (query.length < 3) {
            setSuggestions([]);
            return;
        }
        if (cancelTokenRef.current) {
            cancelTokenRef.current.cancel('Nouvelle requête, annulation de l’ancienne.');
        }
        cancelTokenRef.current = axios.CancelToken.source();
        axios
            .get('https://api-adresse.data.gouv.fr/search/', {
                params: { q: query },
                cancelToken: cancelTokenRef.current.token,
            })
            .then((response) => {
                setSuggestions(response.data.features || []);
            })
            .catch((error) => {
                if (axios.isCancel(error)) {
                    console.log('Requête annulée :', error.message);
                } else {
                    console.error(error);
                }
            });
    }, [query]);

    useEffect(() => {
        console.log(secondFormData)
        if (
            secondFormData.location_coordinates_gps_x &&
            secondFormData.location_coordinates_gps_y
        ) {
            const lat = parseFloat(secondFormData.location_coordinates_gps_x);
            const lng = parseFloat(secondFormData.location_coordinates_gps_y);
            console.log("Coordonnées :", lat, lng);


            // Vérifier que lat et lng sont des nombres finis
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
                setViewState((prev) => ({
                    ...prev,
                    latitude: lat,
                    longitude: lng,
                    zoom: 15,
                    transitionDuration: 1000,
                }));
            } else {
                console.warn("Les coordonnées converties ne sont pas valides:", lat, lng);
            }
        }
    }, [
        secondFormData.location_coordinates_gps_x,
        secondFormData.location_coordinates_gps_y
    ]);

    const handleQueryChange = (e: ChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value);
    };

    const handleSuggestionSelect = (suggestion: any) => {
        const { label, city, postcode } = suggestion.properties;
        updateSecondFormData('address', label);
        updateSecondFormData('city', city);
        updateSecondFormData('postal_code', postcode);
        if (suggestion.geometry?.coordinates) {
            const [lon, lat] = suggestion.geometry.coordinates;
            updateSecondFormData('location_coordinates_gps_x', lat);
            updateSecondFormData('location_coordinates_gps_y', lon);
        }
        setQuery(label);
        setSuggestions([]);
    };
    const lat = parseFloat(secondFormData.location_coordinates_gps_x);
    const lng = parseFloat(secondFormData.location_coordinates_gps_y);
    return (
        <>
            {/* Navigation */}
            <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Button
                    size="lg"
                    variant="outlined"
                    color="neutral"
                    onClick={async () => {
                        setStep((prev) => prev - 1)
                    }}
                    startDecorator={<ChevronLeft />}
                >
                    Précédent
                </Button>
                <Button
                    size="lg"
                    variant="solid"
                    onClick={async () => {
                        if(await validateStep2())
                        {
                            setStep((prev) => prev + 1)
                        }
                    }}
                    endDecorator={<ChevronRight />}
                >
                    Suivant
                </Button>
            </Stack>

            <Card variant="outlined" sx={{ p: 3, mt: 2 }}>
                <Typography level="h4" sx={{ mb: 2 }}>
                    Informations sur le lieu
                </Typography>
                <Grid container spacing={2}>
                    {/* Colonne formulaire (50%) */}
                    <Grid xs={12} md={6}>
                        <Stack spacing={2}>
                            <FormControl>
                                <FormLabel>Localisation</FormLabel>
                                <Select 
                                    placeholder="Sélectionner une localisation"
                                    value={secondFormData.localisation || ''}
                                    onChange={(e, newValue) => updateSecondFormData('localisation', newValue)}
                                
                                >
                                    <Option value="">Sélectionnez une localisation</Option>

                                    {localisations.map((loc: any) => (
                                        <Option key={loc.id} value={`/localisations/${loc.id}`}>
                                            {loc.name}
                                        </Option>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel>Nom du lieu</FormLabel>
                                <Input
                                    placeholder="Saisissez le nom du lieu"
                                    value={secondFormData.location_name || ''}
                                    onChange={(e) => updateSecondFormData('location_name', e.target.value)}
                                />
                            </FormControl>
                            <FormControl ref={autocompleteRef} sx={{ position: 'relative' }}>
                                <FormLabel>Adresse</FormLabel>
                                <Input
                                    placeholder="Saisissez une adresse"
                                    value={query}
                                    onChange={handleQueryChange}
                                    onBlur={() => setTimeout(() => setSuggestions([]), 100)}
                                />
                                {suggestions.length > 0 && (
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            mt: 1,
                                            zIndex: 10,
                                            maxHeight: 200,
                                            overflowY: 'auto',
                                            borderRadius: 'sm',
                                        }}
                                    >
                                        {suggestions.map((suggestion, index) => (
                                            <Box
                                                key={index}
                                                onMouseDown={() => handleSuggestionSelect(suggestion)}
                                                sx={{
                                                    p: 1,
                                                    cursor: 'pointer',
                                                    '&:hover': { backgroundColor: 'neutral.200' },
                                                }}
                                            >
                                                {suggestion.properties.label}
                                            </Box>
                                        ))}
                                    </Card>
                                )}
                            </FormControl>
                        </Stack>
                    </Grid>

                    {/* Colonne carte (50%) */}
                    <Grid xs={12} md={6}>
                        <Box
                            sx={{
                                height: 300,
                                width: '100%',
                                borderRadius: 'sm',
                                overflow: 'hidden',
                                border: '1px solid',
                                borderColor: 'neutral.outlinedBorder',
                            }}
                        >
                            <Map
                                mapboxAccessToken="pk.eyJ1IjoiYXJuYXVkYmFnbmlzIiwiYSI6ImNqc25rcXJ1bjAyeTU0M3FoamF2cXdiMXgifQ.YVSW_BmKa3NngikAOKg1bA"
                                initialViewState={viewState}
                                style={{width: 600, height: 400}}
                                {...viewState}
                                mapStyle="mapbox://styles/mapbox/streets-v11"
                            >
                                {Number.isFinite(lat) && Number.isFinite(lng) && (
                                    <Marker longitude={lng} latitude={lat} anchor="bottom">
                                        <div style={{ backgroundColor: '#10122E', padding: '1px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <img src={Logo} alt="logo colored" style={{ display: 'block',  width: '20px', height: '20px' }}/>
                                        </div>
                                    </Marker>
                                )}
                            </Map>
                        </Box>
                    </Grid>
                </Grid>
            </Card>
        </>
    );
};

export default EventLocationStep;